/**
 * EventDetailPage — Public event detail page.
 *
 * EventCore v1.1 structure order:
 *   1) Hero (4:3, max-h-[320px] mobile, no overlay badges)
 *   2) Title
 *   3) Meta (venue + date/time)
 *   4) Short description (inviting intro)
 *   5) Thumbnail strip (max 4 visible, click → lightbox)
 *   6) Full description
 *   7) Price (must appear before CTA)
 *   8) Booking / Interest CTA (last)
 *
 * Images: media_assets pipeline only — no legacy fields.
 * Lens tone: calm, inviting, not catalogue.
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { HeroCarousel, type HeroImage } from '@/components/place/HeroCarousel';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { usePublicEvent } from '@/hooks/useProducerEvents';
import { usePublicEventOccurrences } from '@/hooks/useEventOccurrences';
import { useEventPublicStats } from '@/hooks/useEventPublicStats';
import { useProducer } from '@/hooks/useProducer';
import { useCreateBooking } from '@/hooks/useEventBookings';
import { useCoverImageUrl, useGalleryUrls } from '@/hooks/useMediaAssets';
import { Seo } from '@/components/common/Seo';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  ArrowLeft,
  Calendar,
  CalendarDays,
  CalendarPlus,
  Share2,
  MapPin,
  Clock,
  UserPlus,
  User,
  Wine,
  ShoppingBasket,
} from 'lucide-react';

import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EventBookingPanel } from '@/components/events/EventBookingPanel';
import { LearningPointsSection } from '@/components/events/LearningPointsSection';
import { EventTypeBadge } from '@/components/events/EventTypeBadge';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { EventGalleryLightbox, type EventGalleryLightboxHandle } from '@/components/events/EventGalleryLightbox';

import {
  formatTimeRow,
  formatEventTimingSummary,
  type TimingMode,
} from '@/lib/events/eventCardUtils';
import { resolveVenueText } from '@/lib/venue/resolveVenueText';
import { compareVenue } from '@/lib/venue/venueShadowCompare';
import { features } from '@/config/features';
import { getPriceCopy } from '@/lib/events/priceCopy';
import { RichTextRenderer } from '@/components/common/RichTextRenderer';
import { useShare } from '@/hooks/useShare';
import { AuthGateCard } from '@/features/activity/AuthGateCard';
import { downloadVCard } from '@/lib/vcard';
import { stripHtmlToText } from '@/lib/text';
import { buildEventIcs, triggerIcsDownload } from '@/lib/ics';
import { createEventJsonLd } from '@/lib/seo';

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const { share } = useShare();

  // --- ALL hooks called unconditionally, gated via `enabled` ---
  const { data: event, isLoading: eventLoading } = usePublicEvent(eventId);
  const { data: occurrences, isLoading: occurrencesLoading } = usePublicEventOccurrences(eventId);
  const { producer, loading: producerLoading } = useProducer(event?.producer_id || '');
  const eventPlaceId = ((event as any)?.place_id as string | null | undefined) ?? (producer?.place_id ?? null);
  const { data: eventPlace } = useQuery({
    queryKey: ['event-place-context', eventPlaceId],
    enabled: !!eventPlaceId,
    queryFn: async (): Promise<{ slug: string; name: string } | null> => {
      const { data, error } = await supabase
        .from('places')
        .select('slug, name')
        .eq('id', eventPlaceId!)
        .maybeSingle();
      if (error) throw error;
      if (!data?.slug || !data?.name) return null;
      return { slug: data.slug, name: data.name };
    },
    staleTime: 5 * 60_000,
  });
  const createBooking = useCreateBooking();
  const galleryRef = useRef<EventGalleryLightboxHandle>(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);
  const isMobile = useIsMobile();
  const bookingStartedTracked = useRef<Set<string>>(new Set());

  // ── Analytics: activity_opened (once per event) ──
  useTrackOnce('activity_opened', {
    activity_id: event?.id ?? '',
    activity_slug: null,
    event_type: event?.event_type ?? null,
    place_id: (event as any)?.place_id ?? null,
    producer_id: event?.producer_id ?? null,
    href: location.pathname + location.search,
  }, event?.id);

  // ── Analytics: booking_started (when overlay opens) ──
  useEffect(() => {
    if (!detailsOpen || !event?.id) return;
    const key = event.id;
    if (bookingStartedTracked.current.has(key)) return;
    bookingStartedTracked.current.add(key);
    track('booking_started', {
      activity_id: event.id,
      producer_id: event.producer_id ?? null,
      place_id: (event as any)?.place_id ?? null,
      booking_mode: (event as any)?.booking_mode ?? null,
      price_ore: event.price_ore ?? null,
      max_spots: event.max_spots ?? null,
    });
  }, [detailsOpen, event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data?.user?.id ?? null);
      setAuthChecked(true);
    });
  }, []);

  const { data: hasExistingBooking = false } = useQuery({
    queryKey: ['user-event-booking', eventId, userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('event_bookings')
        .select('id')
        .eq('event_id', eventId!)
        .eq('user_id', userId!)
        .neq('status', 'cancelled')
        .limit(1);
      return (data?.length ?? 0) > 0;
    },
    enabled: !!eventId && !!userId,
  });

  const { data: stats, refetchStats } = useEventPublicStats(eventId, undefined);

  // Images from media_assets (SoT)
  const mediaCover = useCoverImageUrl('event', eventId, 640);
  const mediaGalleryFull = useGalleryUrls('event', eventId, 640);
  const mediaGalleryThumb = useGalleryUrls('event', eventId, 320);
  // Producer cover as fallback when activity has no images
  const producerFallbackCover = useCoverImageUrl('producer', !mediaCover ? (event?.producer_id ?? null) : null, 640);

  // OG image: 1280 variant for social sharing, fallback: event → producer → Farmly default
  // See docs/seo/social-sharing.md for canonical fallback order
  const ogEventCover = useCoverImageUrl('event', eventId, 1280);
  const ogProducerCover = useCoverImageUrl('producer', !ogEventCover ? (event?.producer_id ?? null) : null, 1280);
  const ogImage = ogEventCover || ogProducerCover || null;

  // --- Derived values ---
  const isRecurring = event?.is_recurring && occurrences && occurrences.length > 0;
  const isLoading = eventLoading || occurrencesLoading || (event && producerLoading);

  const coverImage = mediaCover ?? producerFallbackCover;
  const galleryImages: string[] = mediaGalleryFull;

  // HeroCarousel images for mobile swipe (must be before early returns)
  const heroCarouselImages: HeroImage[] = useMemo(() => {
    const set: string[] = [];
    if (coverImage) set.push(coverImage);
    galleryImages.forEach(img => { if (!set.includes(img)) set.push(img); });
    return set.map((src, i) => ({
      id: `event-img-${i}`,
      src,
      alt: i === 0 ? (event?.title ?? '') : `Bild ${i + 1}`,
    }));
  }, [coverImage, galleryImages, event?.title]);

  const handleHeroIndexChange = useCallback(
    (index: number) => {
      setHeroIndex(index);
      if (!event?.id) return;
      track("event_hero_image_changed", {
        event_id: event.id,
        index,
        images_count: heroCarouselImages.length,
      });
    },
    [event?.id, heroCarouselImages.length],
  );

  // eventJsonLd must be a hook called unconditionally, before the early returns
  // below (Rules of Hooks) -- `event` may still be undefined here, so this is
  // self-contained and null-safe rather than reusing ogDescription/canonicalUrl/
  // practicalVenueLabel, which are computed further down once `event` is
  // guaranteed non-null. Fixes React error #310 (hook-count mismatch) that
  // crashed every /event/:id page.
  const eventJsonLd = useMemo(() => {
    if (!event) return null;
    const description = stripHtmlToText(event.description) || event.title;
    const url = `${typeof window !== 'undefined' ? window.location.origin : 'https://farmly.se'}/event/${eventId}`;
    const { venue_text: venueLabel } = resolveVenueText({
      use_producer_address: (event as any).use_producer_address,
      custom_address: (event as any).custom_address,
      producer_name: producer?.name ?? null,
      producer_city: producer?.city ?? null,
    });
    return createEventJsonLd({
      name: event.title,
      startDate: event.start_date,
      endDate: event.end_date ?? null,
      description: description || null,
      url,
      image: ogImage,
      locationName: venueLabel || producer?.city || null,
      organizerName: producer?.name ?? null,
      priceOre: event.price_ore ?? null,
    });
  }, [event, eventId, ogImage, producer]);

  // Capacity debug — single compact line, DEV only
  if (import.meta.env.DEV && event?.id) {
    console.debug('[EventDetailPage] isFull:', { id: event.id, stats_is_full: stats?.is_full, event_is_full: event.is_full });
  }

  // --- Early returns ---
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8">
          <Skeleton className="h-64 w-full mb-6 rounded-2xl" />
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-24 w-full" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col">
        <SiteHeader density="compact" />
        <main className="flex-1 container py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Aktiviteten hittades inte</h1>
          <p className="text-muted-foreground mb-6">
            Denna aktivitet finns inte eller är inte längre tillgänglig.
          </p>
          <Button asChild>
            <Link to="/explore">Utforska fler upplevelser</Link>
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // --- Audience gate ---
  const eventAudience = (event as any)?.audience as string | undefined;
  const isAuthGated = eventAudience === 'authenticated' && !userId && authChecked;

  // --- Canonical helpers ---
  const timingMode = (event as any).timing_mode as TimingMode | undefined;
  const bookingMode: 'none' | 'optional' | 'required' = event.booking_mode ?? 'none';
  const isBookable = bookingMode !== 'none';
  
  // isFull computed from RPC stats only (SECURITY DEFINER, RLS-safe)
  // Fallback to false if stats unavailable (RPC not yet deployed or event not public)
  const isFull = Boolean(stats?.is_full);
  const timeRow = formatTimeRow(timingMode || 'date_time', event.start_date, event.end_date);
  // Host label — separate from venue (never shown on MapPin row)
  const hostName = producer?.name ?? null;
  const hostHref = producer ? `/producer/${producer.slug || producer.id}` : null;
  const placeHref = eventPlace?.slug ? `/place/${eventPlace.slug}` : null;
  const placeName = eventPlace?.name ?? null;
  const backHref = hostHref ?? placeHref ?? '/explore';
  const backLabel = hostName
    ? `Tillbaka till ${hostName}`
    : placeName
      ? `Tillbaka till ${placeName}`
      : 'Tillbaka till utforska';

  const handleHostClick = () => {
    if (!producer) return;
    track('producer_link_clicked', {
      producer_id: producer.id,
      event_id: event.id,
      source_surface: 'event_page',
    });
  };

  // Practical meeting location (separate from Farmly place context)
  const { venue_text: practicalVenueLabel, venue_kind: practicalVenueKind } = resolveVenueText({
    use_producer_address: (event as any).use_producer_address,
    custom_address: (event as any).custom_address,
    producer_name: producer?.name ?? null,
    producer_city: producer?.city ?? null,
  });
  const showPracticalVenue = practicalVenueKind !== 'none';

  // Shadow compare: compare legacy venue (not host!) vs SoT venue
  const legacyVenueLabel = (event as any).custom_address?.trim() || producer?.city || null;
  compareVenue('event_detail', event.id, legacyVenueLabel, practicalVenueLabel, practicalVenueKind);

  const handleShare = async () => {
  await share({
    kind: "event",
    name: event.title,
    id: event.id,
  });
};

  const heroImage = coverImage || galleryImages[0] || null;

  // Build full image set for lightbox (cover first, then gallery, no dupes)
  const imageSet: string[] = [];
  if (coverImage) imageSet.push(coverImage);
  galleryImages.forEach(img => { if (!imageSet.includes(img)) imageSet.push(img); });

  // Thumbnail strip: max 4 visible, uses 320-variant
  const thumbUrls = mediaGalleryThumb;
  const fullUrls = mediaGalleryFull;
  const stripMax = 4;
  const showOverlay = thumbUrls.length > stripMax;
  const visibleThumbs = showOverlay ? thumbUrls.slice(0, stripMax - 1) : thumbUrls.slice(0, stripMax);
  const extraCount = thumbUrls.length - (stripMax - 1);

  const occCount = occurrences?.length ?? 0;
  const timingSummary = formatEventTimingSummary(
    (timingMode || 'date_time') as TimingMode,
    event.start_date,
    event.end_date,
    (event as any).outing_tags || [],
  );
  const dateSummary = isRecurring
    ? `${occCount} kommande ${occCount === 1 ? 'tillfälle' : 'tillfällen'}`
    : timingSummary;

  const ctaLabel = isFull ? 'Fullbokat' : (bookingMode === 'required' ? 'Boka plats' : 'Anmäl intresse');
  const priceCopy = getPriceCopy(event as any);

  // Helper: thumbnail index → lightbox index (offset by cover)
  const thumbToLightboxIdx = (thumbIdx: number) => coverImage ? thumbIdx + 1 : thumbIdx;

  // Calendar button: only for public date_time events with a start_date
  const showCalendarBtn = event.status === 'public' && timingMode === 'date_time' && !!event.start_date;
  const handleAddToCalendar = () => {
    const venueText = (event as any).venue_text ?? practicalVenueLabel ?? undefined;
    const ics = buildEventIcs({
      uid: `farmly-event-${event.id}@farmly.se`,
      title: event.title,
      startDate: event.start_date,
      endDate: event.end_date ?? null,
      description: stripHtmlToText(event.description) ?? null,
      location: venueText,
    });
    triggerIcsDownload(event.title, ics);
  };

  // OG description: strip HTML, truncate handled by Seo component
  const ogDescription = stripHtmlToText(event.description) || event.title;

  const canonicalUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://farmly.se'}/event/${eventId}`;

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title={event.title}
        description={ogDescription}
        canonicalUrl={canonicalUrl}
        image={ogImage}
        imageAlt={event.title}
        ogType="website"
        jsonLd={eventJsonLd}
      />
      <SiteHeader density="compact" />

      <main className="flex-1">
        {/* ═══ MOBILE LAYOUT (< lg) ═══ */}
        <div className="lg:hidden">
          {/* EventCore v1.1 §1 — Hero (4:3, max-h-[320px], swipeable when multi-image) */}
          <section className="px-4 sm:px-6 pt-4">
            <div data-testid="public-event-hero" className="relative aspect-[4/3] w-full max-h-[320px] rounded-2xl overflow-hidden bg-muted">
              {heroCarouselImages.length > 1 ? (
                <HeroCarousel
                  images={heroCarouselImages}
                  className="absolute inset-0 w-full h-full"
                  onIndexChange={handleHeroIndexChange}
                  activeIndex={heroIndex}
                />
              ) : heroImage ? (
                <button
                  onClick={() => galleryRef.current?.open(0)}
                  className="w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
                  aria-label="Visa bild i fullstorlek"
                >
                  <img data-testid="public-event-hero-image" src={heroImage} alt={event.title} className="w-full h-full object-cover" />
                </button>
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                  <Calendar className="h-12 w-12 text-muted-foreground/30" />
                </div>
              )}
            </div>
          </section>

          {/* EventCore v1.1 §2–§4 — Title, Meta, Short description */}
          <div className="container pt-5 pb-2 space-y-3">
            <div className="flex items-center gap-2">
              {event.event_type === 'course' && <EventTypeBadge type="course" />}
              <h1 data-testid="public-event-title" className="font-display text-2xl sm:text-3xl font-semibold text-foreground leading-tight">
                {event.title}
              </h1>
            </div>
            {((event as any).includes_farm_sale || (event as any).includes_farm_sale_alcohol) && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {(event as any).includes_farm_sale && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" />
                    Handla från gården
                  </span>
                )}
                {(event as any).includes_farm_sale_alcohol && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" />
                    Dryck
                  </span>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              {hostName && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <User aria-hidden className="h-3.5 w-3.5 shrink-0" />
                  <span>Värd: </span>
                  {hostHref ? (
                    <Link
                      to={hostHref}
                      data-testid="public-event-host-link"
                      onClick={handleHostClick}
                      className="underline-offset-2 hover:underline hover:text-foreground transition-colors"
                    >
                      {hostName}
                    </Link>
                  ) : (
                    <span>{hostName}</span>
                  )}
                </div>
              )}
              {showPracticalVenue && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin aria-hidden className="h-3.5 w-3.5 shrink-0" />
                  <span>Här ses vi: </span>
                  <span>{practicalVenueLabel}</span>
                </div>
              )}
              {placeHref && placeName ? (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin aria-hidden className="h-3.5 w-3.5 shrink-0" />
                  <span>I området: </span>
                  <Link to={placeHref} data-testid="activity-place-link" className="underline-offset-2 hover:underline hover:text-foreground transition-colors">
                    {placeName}
                  </Link>
                </div>
              ) : null}
              {timeRow && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock aria-hidden className="h-3.5 w-3.5 shrink-0" />
                  <span>{timeRow}</span>
                </div>
              )}
            </div>
            {/* short_description removed — description is SoT */}
          </div>

          {isAuthGated ? (
            <div className="container py-8" data-testid="audience-gate-mobile">
              <AuthGateCard returnTo={location.pathname} className="max-w-md mx-auto" />
            </div>
          ) : (
          <>
          {/* EventCore v1.1 §5 — Thumbnail strip (max 4, synced with hero on mobile) */}
          {thumbUrls.length > 0 && (
            <div className="container py-3">
              <div className="flex gap-2">
                {visibleThumbs.map((url, i) => {
                  // Map thumb index to hero carousel index (cover occupies index 0 if present)
                  const heroIdx = thumbToLightboxIdx(i);
                  return (
                    <button
                      key={url}
                      onClick={() => {
                        if (isMobile && heroCarouselImages.length > 1) {
                          setHeroIndex(heroIdx);
                        } else {
                          galleryRef.current?.open(heroIdx);
                        }
                      }}
                      className={cn(
                        "shrink-0 rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
                        isMobile && heroCarouselImages.length > 1 && heroIndex !== heroIdx && "opacity-60",
                        isMobile && heroCarouselImages.length > 1 && heroIndex === heroIdx && "ring-2 ring-primary/50",
                      )}
                    >
                      <img
                        src={url}
                        alt={`Bild ${i + 1}`}
                        className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
                {showOverlay && (
                  <button
                    onClick={() => {
                      const heroIdx = thumbToLightboxIdx(stripMax - 1);
                      if (isMobile && heroCarouselImages.length > 1) {
                        setHeroIndex(heroIdx);
                      } else {
                        galleryRef.current?.open(heroIdx);
                      }
                    }}
                    className={cn(
                      "shrink-0 rounded-xl overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
                      isMobile && heroCarouselImages.length > 1 && heroIndex !== thumbToLightboxIdx(stripMax - 1) && "opacity-60",
                      isMobile && heroCarouselImages.length > 1 && heroIndex === thumbToLightboxIdx(stripMax - 1) && "ring-2 ring-primary/50",
                    )}
                  >
                    <img
                      src={thumbUrls[stripMax - 1]}
                      alt={`Bild ${stripMax}`}
                      className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">+{extraCount} bilder</span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* EventCore v1.1 §6 — Full description */}
          <div className="container py-4">
            <Link
              to={backHref}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors mb-6"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {backLabel}
            </Link>

            <div className="max-w-2xl mx-auto space-y-8">
              {/* Date card */}
              {!!timingSummary && (
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    {isRecurring ? (
                      <>
                        <CalendarDays className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium">Datum & tider</p>
                          <p className="text-sm text-muted-foreground">{dateSummary}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium">Tid</p>
                          <p className="text-sm text-muted-foreground">{timingSummary}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {event.description && (
                <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6">
                  <RichTextRenderer html={event.description ?? ""} />
                </div>
              )}

              {/* Course: "Det här lär du dig" */}
              {event.event_type === 'course' && (
                <LearningPointsSection learningPoints={(event as any).learning_points} />
              )}

              {/* Course series: booking covers all occurrences */}
              {event.event_type === 'course' && isRecurring && isBookable && (
                <p className="text-sm text-muted-foreground italic">
                  Bokningen gäller alla tillfällen i kursen.
                </p>
              )}

              {/* EventCore v1.1 §7+§8 — CTA card with price inside */}
              {isBookable && hasExistingBooking ? (
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-6 space-y-3 text-center">
                    <CheckCircle className="h-10 w-10 text-primary mx-auto" />
                    <h2 className="text-xl font-semibold">Du är redan anmäld</h2>
                    <p className="text-muted-foreground">Du hittar detaljer under Mina bokningar.</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button asChild size="lg"><Link to="/app/customer/bookings">Visa bokning</Link></Button>
                      <Button size="lg" variant="outline" onClick={() => setDetailsOpen(true)}>Visa datum & detaljer</Button>
                    </div>
                  </CardContent>
                </Card>
              ) : isBookable ? (
                <Card className={isFull ? "border-muted" : "border-primary/20 bg-primary/5"}>
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-xl font-semibold">{isFull ? 'Fullbokat' : 'Vill du vara med?'}</h2>
                    <p className="text-muted-foreground">
                      {isFull
                        ? 'Tyvärr finns inga platser kvar.'
                        : bookingMode === 'required' ? 'Boka din plats nedan.' : 'Anmäl intresse så att värden vet ungefär.'}
                    </p>
                    {priceCopy && (
                      <div className="space-y-0.5" data-testid="public-event-price-mobile">
                        <p className="text-sm font-medium">{priceCopy.primary}</p>
                        {priceCopy.secondary && <p className="text-xs text-muted-foreground">{priceCopy.secondary}</p>}
                      </div>
                     )}
                     {(event as any).includes_farm_sale && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" />
                         <span>Handla från gården i samband med aktiviteten</span>
                       </div>
                     )}
                     {(event as any).includes_farm_sale_alcohol && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <Wine aria-hidden className="h-4 w-4 shrink-0" />
                          <span>Dryck i samband med aktiviteten</span>
                       </div>
                     )}
                     <div className="flex flex-col sm:flex-row gap-3">
                      <Button className="w-full sm:w-auto" size="lg" onClick={() => setDetailsOpen(true)} disabled={isFull} data-testid="public-event-cta-mobile">{ctaLabel}</Button>
                      <Button className="w-full sm:w-auto" size="lg" variant="outline" onClick={handleShare}><Share2 className="h-4 w-4 mr-2" />Dela</Button>
                      {showCalendarBtn && (
                        <Button className="w-full sm:w-auto" size="lg" variant="outline" onClick={handleAddToCalendar}><CalendarPlus className="h-4 w-4 mr-2" />Lägg till i kalender</Button>
                      )}
                    </div>
                    {producer?.name && (producer.phone || producer.email || producer.website) && (
                      <button
                        type="button"
                        onClick={() => downloadVCard(producer.name, { phone: producer.phone ?? undefined, email: producer.email ?? undefined, website: producer.website ?? undefined })}
                        className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                      >
                        <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>Lägg till kontakt</span>
                      </button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6 space-y-3">
                     <h2 className="text-lg font-semibold">Bra att veta</h2>
                     <p className="text-muted-foreground">Det här är öppet att komma förbi. Ingen anmälan behövs.</p>
                     {(event as any).includes_farm_sale && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" />
                         <span>Handla från gården i samband med aktiviteten</span>
                       </div>
                     )}
                     {(event as any).includes_farm_sale_alcohol && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <Wine aria-hidden className="h-4 w-4 shrink-0" />
                          <span>Dryck i samband med aktiviteten</span>
                       </div>
                     )}
                    {priceCopy && (
                      <div className="space-y-0.5" data-testid="public-event-price-mobile">
                        <p className="text-sm font-medium">{priceCopy.primary}</p>
                        {priceCopy.secondary && <p className="text-xs text-muted-foreground">{priceCopy.secondary}</p>}
                      </div>
                    )}
                     <div className="flex flex-wrap gap-3">
                       <Button variant="outline" onClick={handleShare}><Share2 className="h-4 w-4 mr-2" />Dela</Button>
                       {showCalendarBtn && (
                         <Button variant="outline" onClick={handleAddToCalendar}><CalendarPlus className="h-4 w-4 mr-2" />Lägg till i kalender</Button>
                       )}
                     </div>
                    {producer?.name && (producer.phone || producer.email || producer.website) && (
                      <button
                        type="button"
                        onClick={() => downloadVCard(producer.name, { phone: producer.phone ?? undefined, email: producer.email ?? undefined, website: producer.website ?? undefined })}
                        className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                      >
                        <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>Lägg till kontakt</span>
                      </button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        {/* ═══ DESKTOP LAYOUT (lg+): two-column grid ═══ */}
        <div className="hidden lg:block">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <Link
              to={backHref}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors mb-6"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {backLabel}
            </Link>

            <div className="grid gap-10" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
              {/* ── Left column ── */}
              <div className="space-y-6 min-w-0">
                {/* EventCore v1.1 §1 — Hero (4:3, no overlay badges) */}
                <div data-testid="public-event-hero" className="relative aspect-[4/3] w-full rounded-2xl overflow-hidden bg-muted" style={{ maxHeight: 'clamp(320px, 45vh, 420px)' }}>
                  {heroImage ? (
                    <button
                      onClick={() => galleryRef.current?.open(0)}
                      className="w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary group cursor-pointer"
                      aria-label="Visa bild i fullstorlek"
                    >
                      <img data-testid="public-event-hero-image" src={heroImage} alt={event.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                    </button>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                      <Calendar className="h-16 w-16 text-muted-foreground/30" />
                    </div>
                  )}
                </div>

                {/* EventCore v1.1 §2–§3 — Title + Meta */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {event.event_type === 'course' && <EventTypeBadge type="course" />}
                    <h1 data-testid="public-event-title" className="font-display text-5xl font-semibold text-foreground leading-tight">{event.title}</h1>
                  </div>
                  {((event as any).includes_farm_sale || (event as any).includes_farm_sale_alcohol) && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(event as any).includes_farm_sale && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                          <ShoppingBasket aria-hidden className="h-3.5 w-3.5 shrink-0" />
                          Handla från gården
                        </span>
                      )}
                      {(event as any).includes_farm_sale_alcohol && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                          <Wine aria-hidden className="h-3.5 w-3.5 shrink-0" />
                          Dryck
                        </span>
                      )}
                    </div>
                  )}
                  {hostName && (
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <User aria-hidden className="h-4 w-4 shrink-0" />
                      <span>Värd: </span>
                      {hostHref ? (
                        <Link
                          to={hostHref}
                          data-testid="public-event-host-link"
                          onClick={handleHostClick}
                          className="underline-offset-2 hover:underline hover:text-foreground transition-colors"
                        >
                          {hostName}
                        </Link>
                      ) : (
                        <span>{hostName}</span>
                      )}
                    </div>
                  )}
                  {showPracticalVenue && (
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <MapPin aria-hidden className="h-4 w-4 shrink-0" />
                      <span>Här ses vi: </span>
                      <span>{practicalVenueLabel}</span>
                    </div>
                  )}
                  {placeHref && placeName ? (
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <MapPin aria-hidden className="h-4 w-4 shrink-0" />
                      <span>I området: </span>
                      <Link to={placeHref} data-testid="activity-place-link" className="underline-offset-2 hover:underline hover:text-foreground transition-colors">
                        {placeName}
                      </Link>
                    </div>
                  ) : null}
                  {timeRow && (
                    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
                      <Clock aria-hidden className="h-4 w-4 shrink-0" />
                      <span>{timeRow}</span>
                    </div>
                  )}
                </div>

                {/* short_description removed — description is SoT */}

                {!isAuthGated && (
                  <>
                {/* EventCore v1.1 §5 — Thumbnail strip */}
                {thumbUrls.length > 0 && (
                  <div className="flex gap-2.5">
                    {visibleThumbs.map((url, i) => (
                      <button
                        key={url}
                        onClick={() => galleryRef.current?.open(thumbToLightboxIdx(i))}
                        className="shrink-0 rounded-xl overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <img
                          src={url}
                          alt={`Bild ${i + 1}`}
                          className="h-20 w-auto aspect-[4/3] object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                    {showOverlay && (
                      <button
                        onClick={() => galleryRef.current?.open(thumbToLightboxIdx(stripMax - 1))}
                        className="shrink-0 rounded-xl overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <img
                          src={thumbUrls[stripMax - 1]}
                          alt={`Bild ${stripMax}`}
                          className="h-20 w-auto aspect-[4/3] object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-white text-sm font-medium">+{extraCount} bilder</span>
                        </div>
                      </button>
                    )}
                  </div>
                )}

                {/* EventCore v1.1 §6 — Full description */}
                {event.description && (
                  <div className="prose prose-neutral max-w-none prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-hr:my-6">
                    <RichTextRenderer html={event.description ?? ""} />
                  </div>
                )}

                {/* Course: "Det här lär du dig" */}
                {event.event_type === 'course' && (
                  <LearningPointsSection learningPoints={(event as any).learning_points} />
                )}

                {/* Course series: booking covers all occurrences */}
                {event.event_type === 'course' && isRecurring && isBookable && (
                  <p className="text-sm text-muted-foreground italic">
                    Bokningen gäller alla tillfällen i kursen.
                  </p>
                )}
                  </>
                )}
              </div>

              {/* ── Right column: date card + price + CTA (sticky) ── */}
              <div className="lg:sticky lg:top-24 self-start space-y-6">
                {isAuthGated ? (
                  <AuthGateCard returnTo={location.pathname} data-testid="audience-gate-desktop" />
                ) : (
                  <>
                {!!timingSummary && (
                  <Card>
                    <CardContent className="flex items-center gap-3 p-4">
                      {isRecurring ? (
                        <>
                          <CalendarDays className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <div>
                            <p className="font-medium">Datum & tider</p>
                            <p className="text-sm text-muted-foreground">{dateSummary}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <div>
                            <p className="font-medium">Tid</p>
                            <p className="text-sm text-muted-foreground">{timingSummary}</p>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* EventCore v1.1 §7+§8 — CTA card with price inside */}
                {isBookable && hasExistingBooking ? (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="p-6 space-y-3 text-center">
                      <CheckCircle className="h-10 w-10 text-primary mx-auto" />
                      <h2 className="text-xl font-semibold">Du är redan anmäld</h2>
                      <p className="text-muted-foreground">Du hittar detaljer under Mina bokningar.</p>
                      <div className="flex flex-col gap-3">
                        <Button asChild size="lg"><Link to="/app/customer/bookings">Visa bokning</Link></Button>
                        <Button size="lg" variant="outline" onClick={() => setDetailsOpen(true)}>Visa datum & detaljer</Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : isBookable ? (
                  <Card className={isFull ? "border-muted" : "border-primary/20 bg-primary/5"}>
                    <CardContent className="p-6 space-y-4">
                      <h2 className="text-xl font-semibold">{isFull ? 'Fullbokat' : 'Vill du vara med?'}</h2>
                      <p className="text-muted-foreground">
                        {isFull
                          ? 'Tyvärr finns inga platser kvar.'
                          : bookingMode === 'required' ? 'Boka din plats nedan.' : 'Anmäl intresse så att värden vet ungefär.'}
                      </p>
                      {priceCopy && (
                        <div className="space-y-0.5" data-testid="public-event-price-desktop">
                          <p className="text-sm font-medium">{priceCopy.primary}</p>
                          {priceCopy.secondary && <p className="text-xs text-muted-foreground">{priceCopy.secondary}</p>}
                        </div>
                      )}
                      {(event as any).includes_farm_sale && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" />
                          <span>Handla från gården i samband med aktiviteten</span>
                        </div>
                      )}
                      {(event as any).includes_farm_sale_alcohol && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Wine aria-hidden className="h-4 w-4 shrink-0" />
                           <span>Dryck i samband med aktiviteten</span>
                        </div>
                      )}
                      <div className="flex flex-col gap-3">
                        <Button className="w-full" size="lg" onClick={() => setDetailsOpen(true)} disabled={isFull} data-testid="public-event-cta-desktop">{ctaLabel}</Button>
                        <Button className="w-full" size="lg" variant="outline" onClick={handleShare}><Share2 className="h-4 w-4 mr-2" />Dela</Button>
                        {showCalendarBtn && (
                          <Button className="w-full" size="lg" variant="outline" onClick={handleAddToCalendar}><CalendarPlus className="h-4 w-4 mr-2" />Lägg till i kalender</Button>
                        )}
                      </div>
                      {producer?.name && (producer.phone || producer.email || producer.website) && (
                        <button
                          type="button"
                          onClick={() => downloadVCard(producer.name, { phone: producer.phone ?? undefined, email: producer.email ?? undefined, website: producer.website ?? undefined })}
                          className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                        >
                          <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span>Lägg till kontakt</span>
                        </button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-6 space-y-3">
                     <h2 className="text-lg font-semibold">Bra att veta</h2>
                     <p className="text-muted-foreground">Det här är öppet att komma förbi. Ingen anmälan behövs.</p>
                     {(event as any).includes_farm_sale && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" />
                         <span>Handla från gården i samband med aktiviteten</span>
                       </div>
                     )}
                     {(event as any).includes_farm_sale_alcohol && (
                       <div className="flex items-center gap-2 text-sm text-muted-foreground">
                         <Wine aria-hidden className="h-4 w-4 shrink-0" />
                         <span>Dryck i samband med aktiviteten</span>
                       </div>
                     )}
                      {priceCopy && (
                        <div className="space-y-0.5" data-testid="public-event-price-desktop">
                          <p className="text-sm font-medium">{priceCopy.primary}</p>
                          {priceCopy.secondary && <p className="text-xs text-muted-foreground">{priceCopy.secondary}</p>}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <Button variant="outline" onClick={handleShare}><Share2 className="h-4 w-4 mr-2" />Dela</Button>
                        {showCalendarBtn && (
                          <Button variant="outline" onClick={handleAddToCalendar}><CalendarPlus className="h-4 w-4 mr-2" />Lägg till i kalender</Button>
                        )}
                      </div>
                      {producer?.name && (producer.phone || producer.email || producer.website) && (
                        <button
                          type="button"
                          onClick={() => downloadVCard(producer.name, { phone: producer.phone ?? undefined, email: producer.email ?? undefined, website: producer.website ?? undefined })}
                          className="flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
                        >
                          <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span>Lägg till kontakt</span>
                        </button>
                      )}
                    </CardContent>
                  </Card>
                )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Lightbox (uses existing EventGalleryLightbox with full swipe/keyboard support) */}
      <EventGalleryLightbox
        ref={galleryRef}
        images={galleryImages}
        coverImage={coverImage}
        showThumbnails={false}
      />

      {/* Booking overlay */}
      {isBookable && (
        <ResponsiveOverlay
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          title={bookingMode === 'required' ? 'Boka plats' : 'Anmäl intresse'}
          description={bookingMode === 'required' ? 'Välj ett tillfälle och fyll i dina uppgifter.' : 'Välj ett tillfälle så att värden vet ungefär.'}
          showHeader
          desktopMaxWidth="xl"
        >
          <div className="overflow-y-auto" data-testid="booking-overlay">
            <EventBookingPanel
              event={event}
              occurrences={occurrences}
              producerName={producer?.name}
              producerSlug={producer?.slug}
              venueText={practicalVenueLabel}
              stats={stats}
              refetchStats={refetchStats}
              createBooking={createBooking}
            />
          </div>
        </ResponsiveOverlay>
      )}

      <SiteFooter />
    </div>
  );
}
